<template>
  <q-dialog v-model="active" @hide="search = ''" position="top" transition-duration="100">
    <q-card style="width: 50vw">
      <q-input
        borderless
        ref="searchInput"
        v-model="search"
        placeholder="Search..."
        class="q-pa-xs"
        autofocus
        clearable
        @clear="search = ''"
        @keydown.down.prevent="focusNext(0)"
        @keydown.tab.prevent="focusNext(0)"
      >
        <template #prepend>
          <q-icon name="search"></q-icon>
        </template>
      </q-input>
      <q-separator />
      <q-card-section>
        <template v-if="filteredOptions.length === 0">
          <div class="text-subtitle2 text-grey-6">No results</div>
        </template>
        <template v-else>
          <div class="text-subtitle2 text-grey-6">Suggestions</div>
          <q-list dense>
            <q-item
              v-for="(option, idx) in filteredOptions"
              :key="idx"
              :id="`command-option-${idx}`"
              v-close-popup
              clickable
              @click="option.action"
              @keydown.down.prevent="focusNext(idx === filteredOptions.length - 1 ? 0 : idx + 1)"
              @keydown.up.prevent="focusNext(idx === 0 ? filteredOptions.length - 1 : idx - 1)"
              @keydown.enter="() => option.action"
              @keydown.esc.stop="focusInput"
              @keydown.delete.stop="focusInput"
            >
              <q-item-section>
                {{ option.label }}
              </q-item-section>
            </q-item>
          </q-list>
        </template>
      </q-card-section>
      <q-separator />
      <q-card-actions class="row items-center q-gutter-x-sm q-pa-md">
        <div class="row items-center q-gutter-x-sm">
          <q-badge :color="$q.dark.isActive ? 'grey-8' : 'grey-4'">
            <q-icon
              name="keyboard_return"
              :class="$q.dark.isActive ? 'text-grey-2' : 'text-grey-8'"
            />
          </q-badge>
          <div class="text-grey-7">To select</div>
        </div>
        <div class="row items-center q-gutter-x-sm">
          <q-badge :color="$q.dark.isActive ? 'grey-8' : 'grey-4'">
            <q-icon :class="$q.dark.isActive ? 'text-grey-2' : 'text-grey-8'" name="arrow_upward" />
          </q-badge>
          <q-badge :color="$q.dark.isActive ? 'grey-8' : 'grey-4'">
            <q-icon
              :class="$q.dark.isActive ? 'text-grey-2' : 'text-grey-8'"
              name="arrow_downward"
            />
          </q-badge>
          <div class="text-grey-7">To navigate</div>
        </div>
        <div class="row items-center q-gutter-x-sm">
          <q-badge :color="$q.dark.isActive ? 'grey-8' : 'grey-4'">
            <kbd :class="$q.dark.isActive ? 'text-grey-2' : 'text-grey-8'"> esc </kbd>
          </q-badge>
          <div class="text-grey-7">To close</div>
        </div>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
export default {
  name: 'CommandPalette',
  data() {
    return {
      active: false,
      search: '',
      handleCommandPaletteKeyboardShortcut: null as ((e: KeyboardEvent) => void) | null,
      routeOptions: [
        {
          label: 'All Users',
          action: () => this.$router.push({ name: 'All Users' })
        },
        {
          label: 'Admin Actions',
          action: () => this.$router.push({ name: 'Admin' })
        },
        {
          label: 'New Pilot',
          action: () => this.$router.push({ name: 'New Pilot' })
        },
        {
          label: 'New Client',
          action: () => this.$router.push({ name: 'CreateClientBillingTypeStep' })
        },
        {
          label: 'Manage Cache',
          action: () => this.$router.push({ name: 'Manage Cache' })
        },
        {
          label: 'Create SAML SSO',
          action: () => this.$router.push({ name: 'SSO Create' })
        },
        {
          label: 'Pipeline Health Dashboard',
          action: () => this.$router.push({ name: 'Pipeline Health Dashboard' })
        },
        {
          label: 'Dashboard',
          action: () => this.$router.push({ name: 'Dashboard' })
        },
        {
          label: 'Market Analytics',
          action: () => this.$router.push({ name: 'Market Analytics' })
        },
        {
          label: 'Query Builder',
          action: () => this.$router.push({ name: 'Datasets' })
        },
        {
          label: 'Rent Roll Parser',
          action: () => this.$router.push({ name: 'Rent Roll Parser' })
        },
        {
          label: 'Copy Property Report',
          action: () => this.$router.push({ name: 'Copy Property Report' })
        },
        {
          label: 'Copy Query Builder Dataset',
          action: () => this.$router.push({ name: 'Copy Query Builder' })
        },
        {
          label: 'Copy Rent Roll Report',
          action: () => this.$router.push({ name: 'Copy Rent Roll' })
        }
      ]
    }
  },
  mounted() {
    this.handleCommandPaletteKeyboardShortcut = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        this.active = !this.active
        if (this.active) {
          this.$tracker.openedCommandPalette()
        }
      }
    }
    window.addEventListener('keydown', this.handleCommandPaletteKeyboardShortcut)
  },
  unmounted() {
    if (this.handleCommandPaletteKeyboardShortcut) {
      window.removeEventListener('keydown', this.handleCommandPaletteKeyboardShortcut)
    }
  },
  computed: {
    filteredOptions() {
      if (this.search === '') {
        return this.routeOptions
      }
      const needle = this.search.toLowerCase()
      return this.routeOptions.filter((_) => _.label.toLowerCase().includes(needle))
    }
  },
  methods: {
    focusNext(idx: number) {
      document.getElementById(`command-option-${idx}`)?.focus()
    },
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput as any
        input?.focus()
      })
    }
  }
}
</script>

<style scoped></style>
